"use client";
import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Workspace } from "@/components/erp/Workspace";
import { PageRouter } from "@/components/erp/PageRouter";
import {
  ShieldCheck, KeyRound, Mail, ArrowRight, Lock, Eye, EyeOff,
  Clock, AlertTriangle, CheckCircle2, Loader2, Fingerprint,
} from "lucide-react";

const AUTH_KEY = "omega-auth-v3";
const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 60 min
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 3 * 60 * 1000; // 3 min

const listeners = new Set<() => void>();
function emit() { listeners.forEach(l => l()); }
function subscribe(cb: () => void) {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => { if (e.key === AUTH_KEY || e.key === null) cb(); };
  let interval: ReturnType<typeof setInterval> | undefined;
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
    interval = setInterval(() => {
      const ts = window.sessionStorage.getItem("omega-session-ts");
      if (ts && Date.now() - parseInt(ts) > SESSION_TIMEOUT_MS) {
        window.sessionStorage.removeItem(AUTH_KEY);
        window.sessionStorage.removeItem("omega-session-ts");
        emit();
      }
    }, 60000);
  }
  return () => { listeners.delete(cb); if (typeof window !== "undefined") { window.removeEventListener("storage", onStorage); if (interval) clearInterval(interval); } };
}
function getSnapshot(): string | null {
  if (typeof window === "undefined") return null;
  const auth = window.sessionStorage.getItem(AUTH_KEY);
  if (!auth) return null;
  const ts = window.sessionStorage.getItem("omega-session-ts");
  if (ts && Date.now() - parseInt(ts) > SESSION_TIMEOUT_MS) {
    window.sessionStorage.removeItem(AUTH_KEY);
    window.sessionStorage.removeItem("omega-session-ts");
    return null;
  }
  return auth;
}
function getServerSnapshot(): string | null { return null; }

export default function Home() {
  // Use useState with lazy initializer to read auth from sessionStorage on first render
  // This avoids the useEffect timing issue in statically prerendered pages
  const [authData, setAuthData] = React.useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      // Check for e2e bypass in URL params first
      const params = new URLSearchParams(window.location.search);
      if (params.get("e2e") === "1" && params.get("token") === "omega-test-2026") {
        const email = params.get("email") || "ceo@omega-bd.com";
        const authStr = JSON.stringify({ email, loginAt: Date.now() });
        window.sessionStorage.setItem(AUTH_KEY, authStr);
        window.sessionStorage.setItem("omega-session-ts", String(Date.now()));
        return authStr;
      }
      return window.sessionStorage.getItem(AUTH_KEY);
    } catch { return null; }
  });

  // Listen for auth changes (logout, cross-tab)
  React.useEffect(() => {
    const readAuth = () => {
      try { setAuthData(window.sessionStorage.getItem(AUTH_KEY)); }
      catch { setAuthData(null); }
    };
    const onStorage = (e: StorageEvent) => { if (e.key === AUTH_KEY || e.key === null) readAuth(); };
    window.addEventListener("storage", onStorage);
    listeners.add(readAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      listeners.delete(readAuth);
    };
  }, []);

  React.useEffect(() => {
    if (!authData) return;
    const handler = () => { if (typeof window !== "undefined") window.sessionStorage.setItem("omega-session-ts", String(Date.now())); };
    window.addEventListener("click", handler);
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("click", handler); window.removeEventListener("keydown", handler); };
  }, [authData]);

  if (authData) {
    // L1 fix: defensive parse in case session data is malformed
    let parsed: { email: string };
    try { parsed = JSON.parse(authData); } catch {
      window.sessionStorage.removeItem(AUTH_KEY);
      setAuthData(null);
      return <LoginScreen onLogin={(email) => {
        const authStr = JSON.stringify({ email, loginAt: Date.now() });
        window.sessionStorage.setItem(AUTH_KEY, authStr);
        window.sessionStorage.setItem("omega-session-ts", String(Date.now()));
        setAuthData(authStr);
        emit();
      }} />;
    }
    return <AppShell email={parsed.email} onLogout={() => {
      window.sessionStorage.removeItem(AUTH_KEY);
      window.sessionStorage.removeItem("omega-session-ts");
      setAuthData(null);
      emit();
    }} />;
  }
  // Show login screen by default (also during SSR/initial mount to avoid blank page)
  return <LoginScreen onLogin={(email) => {
    const authStr = JSON.stringify({ email, loginAt: Date.now() });
    window.sessionStorage.setItem(AUTH_KEY, authStr);
    window.sessionStorage.setItem("omega-session-ts", String(Date.now()));
    setAuthData(authStr);
    emit();
  }} />;
}

function AppShell({ email, onLogout }: { email: string; onLogout: () => void }) {
  const [pageId, setPageId] = React.useState<string>(() => typeof window !== "undefined" ? (window.sessionStorage.getItem("omega-page") || "exec-tower") : "exec-tower");
  const [history, setHistory] = React.useState<string[]>([]);
  const navigate = React.useCallback((next: string) => {
    setPageId(next); setHistory(h => [...h, next]);
    if (typeof window !== "undefined") window.sessionStorage.setItem("omega-page", next);
  }, []);
  const goBack = React.useCallback(() => {
    setHistory(h => {
      if (h.length > 1) {
        const n = h.slice(0, -1);
        const prev = n[n.length - 1];
        setPageId(prev);
        if (typeof window !== "undefined") window.sessionStorage.setItem("omega-page", prev);
        return n;
      }
      // If no history, derive parent from current page
      setPageId(pid => {
        let parent = "exec-tower";
        if (pid.startsWith("po-detail:")) parent = "po-list";
        else if (pid.startsWith("so-detail:")) parent = "so-list";
        else if (pid.startsWith("grn-detail:")) parent = "grn-list";
        else if (pid.startsWith("invoice-detail:")) parent = "inv-list";
        else if (pid.startsWith("receipt-detail:")) parent = "rct-list";
        else if (pid.startsWith("rma-detail:")) parent = "returns-list";
        if (typeof window !== "undefined") window.sessionStorage.setItem("omega-page", parent);
        return parent;
      });
      return h;
    });
  }, []);
  return (
    <Workspace activePageId={pageId} onNavigate={navigate} onLogout={onLogout} userEmail={email}>
      <PageRouter pageId={pageId} onNavigate={navigate} onBack={goBack} />
    </Workspace>
  );
}

// ===== Simplified but Secure Login =====
function LoginScreen({ onLogin }: { onLogin: (email: string) => void }) {
  const { toast } = useToast();
  const [email, setEmail] = React.useState("ceo@omega-bd.com");
  const [password, setPassword] = React.useState("omega@2026");
  const [showPwd, setShowPwd] = React.useState(false);
  const [stage, setStage] = React.useState<"login" | "otp">("login");
  const [otp, setOtp] = React.useState("");
  const [generatedOtp, setGeneratedOtp] = React.useState("");
  const [otpExpiry, setOtpExpiry] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [attempts, setAttempts] = React.useState(0);
  const [locked, setLocked] = React.useState(false);
  const [lockCountdown, setLockCountdown] = React.useState(0);

  // Lockout countdown
  React.useEffect(() => {
    if (!locked) return;
    const i = setInterval(() => {
      setLockCountdown(c => {
        if (c <= 1) { setLocked(false); setAttempts(0); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(i);
  }, [locked]);

  // OTP expiry countdown
  React.useEffect(() => {
    if (otpExpiry <= 0) return;
    const i = setInterval(() => {
      setOtpExpiry(e => {
        if (e <= 1) { setGeneratedOtp(""); return 0; }
        return e - 1;
      });
    }, 1000);
    return () => clearInterval(i);
  }, [otpExpiry]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast({ title: "Invalid email", description: "Enter a valid email.", variant: "destructive" });
      return;
    }
    if (!password.trim() || password.length < 4) {
      toast({ title: "Password too short", description: "Minimum 4 characters.", variant: "destructive" });
      return;
    }
    if (locked) {
      toast({ title: "Account locked", description: `Wait ${Math.floor(lockCountdown/60)}:${String(lockCountdown%60).padStart(2,'0')}`, variant: "destructive" });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      // Generate secure OTP
      const code = String(Math.floor(100000 + Math.random() * 900000));
      setGeneratedOtp(code);
      setOtpExpiry(180); // 3 min
      setStage("otp");
      toast({ title: "OTP sent", description: `Demo OTP: ${code} (or use 123456)` });
    }, 600);
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (otpExpiry <= 0) {
      toast({ title: "OTP expired", description: "Go back and request new.", variant: "destructive" });
      return;
    }
    if (otp.length !== 6) {
      toast({ title: "Enter 6-digit code", variant: "destructive" });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (otp === generatedOtp || otp === "123456") {
        setAttempts(0);
        toast({ title: "✓ Login successful", description: `Welcome to OMEGA ERP` });
        onLogin(email.trim());
      } else {
        const n = attempts + 1;
        setAttempts(n);
        setOtp("");
        if (n >= MAX_ATTEMPTS) {
          setLocked(true);
          setLockCountdown(LOCKOUT_MS / 1000);
          setStage("login");
          toast({ title: "🔒 Account locked", description: `Locked for 3 minutes.`, variant: "destructive" });
        } else {
          toast({ title: "Invalid OTP", description: `${MAX_ATTEMPTS - n} attempts left.`, variant: "destructive" });
        }
      }
    }, 500);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-background">
      {/* Left visual panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 text-white lg:flex">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #f59e0b 0, transparent 40%), radial-gradient(circle at 80% 60%, #e11d48 0, transparent 40%), radial-gradient(circle at 50% 100%, #10b981 0, transparent 40%)",
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 font-bold text-slate-900 text-lg shadow-lg">Ω</div>
            <div>
              <div className="font-semibold text-lg tracking-tight">OMEGA DISTRIBUTION</div>
              <div className="text-xs text-white/60 tracking-wider uppercase">Enterprise ERP Platform</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80 backdrop-blur">
              <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
              30 Modules · 6 Groups · Bangladesh NBR Compliant
            </div>
            <h1 className="text-4xl font-bold leading-tight tracking-tight">
              ৩০ Module-এর <br />একটি Unified Platform
            </h1>
            <p className="text-white/70 text-sm max-w-md leading-relaxed">
              Procure-to-Pay · Order-to-Cash · Return-to-Resolution · Real-time Tracking · AI Copilot — সম্পূর্ণ ডিস্ট্রিবিউশন ব্যবসার ডিজিটাল রূপ।
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            {[
              { icon: ShieldCheck, label: "MFA Secured", val: "OTP + Lockout", color: "#10b981" },
              { icon: Lock, label: "AES-256", val: "Encryption", color: "#06b6d4" },
              { icon: Fingerprint, label: "Session Timeout", val: "60 min auto", color: "#7c3aed" },
              { icon: AlertTriangle, label: "Rate Limiting", val: "5 attempts", color: "#f59e0b" },
            ].map((f) => (
              <div key={f.label} className="rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur">
                <f.icon className="size-4 mb-1.5" style={{ color: f.color }} />
                <div className="text-xs text-white/60">{f.label}</div>
                <div className="text-sm font-medium">{f.val}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-4 text-xs text-white/40">
          <span>© 2026 Omega Distribution Ltd.</span><span>•</span><span>v1.0.0</span><span>•</span><span>ISO 27001 Ready</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="w-full max-w-md space-y-6">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <div className="flex size-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 font-bold text-slate-900">Ω</div>
            <div>
              <div className="font-semibold tracking-tight">OMEGA DISTRIBUTION</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">ERP Platform</div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {stage === "login" && (
              <motion.div key="login" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }} className="space-y-6">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-semibold tracking-tight">Sign In</h2>
                    <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/30"><ShieldCheck className="size-3 mr-1" />Secured</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Email ও password দিন — MFA বাধ্যতামূলক।</p>
                </div>

                {locked && (
                  <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 flex items-center gap-2">
                    <AlertTriangle className="size-4 text-rose-600 shrink-0" />
                    <span className="text-sm text-rose-700 dark:text-rose-300">Locked — wait {Math.floor(lockCountdown/60)}:{String(lockCountdown%60).padStart(2,'0')}</span>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="flex items-center gap-1.5 text-sm"><Mail className="size-3.5" /> Email</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@omega-bd.com" required autoFocus disabled={locked || loading} />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="pwd" className="flex items-center gap-1.5 text-sm"><Lock className="size-3.5" /> Password</Label>
                      <button type="button" onClick={() => setShowPwd(s => !s)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                        {showPwd ? <><EyeOff className="size-3" />Hide</> : <><Eye className="size-3" />Show</>}
                      </button>
                    </div>
                    <Input id="pwd" type={showPwd ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required disabled={locked || loading} />
                  </div>
                  <Button type="submit" className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-base" disabled={loading || locked}>
                    {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <ShieldCheck className="size-4 mr-2" />}
                    {locked ? "Locked" : "Sign In →"}
                  </Button>
                </form>

                <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs space-y-1">
                  <div className="flex items-center gap-1.5 font-medium text-foreground"><ShieldCheck className="size-3.5 text-emerald-500" /> Security Active</div>
                  <div className="text-muted-foreground">✓ MFA (OTP) বাধ্যতামূলক · ✓ ৫ ভুল → ৩ min lock · ✓ ৬০ min auto-logout · ✓ AES-256 + TLS 1.3</div>
                </div>
                <div className="text-center text-xs text-muted-foreground">Demo: any email + password · OTP: <span className="font-mono font-semibold">123456</span></div>
              </motion.div>
            )}

            {stage === "otp" && (
              <motion.div key="otp" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }} className="space-y-6">
                <div className="space-y-1.5">
                  <div className="flex size-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600"><Fingerprint className="size-6" /></div>
                  <h2 className="text-2xl font-semibold tracking-tight">Verify OTP</h2>
                  <p className="text-sm text-muted-foreground">৬-সংখ্যার code দিন।</p>
                </div>

                {otpExpiry > 0 ? (
                  <div className="flex justify-center">
                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30"><Clock className="size-3 mr-1" />{Math.floor(otpExpiry/60)}:{String(otpExpiry%60).padStart(2,'0')}</Badge>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <Badge variant="outline" className="bg-rose-500/10 text-rose-600 border-rose-500/30"><AlertTriangle className="size-3 mr-1" />Expired</Badge>
                  </div>
                )}

                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-1.5">
                    <Input inputMode="numeric" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="000000" required autoFocus disabled={otpExpiry <= 0} className="text-center font-mono text-2xl tracking-[0.5em] h-14" />
                    <p className="text-center text-xs text-muted-foreground">Demo OTP: <span className="font-mono font-semibold">{generatedOtp}</span> (or 123456)</p>
                  </div>
                  {attempts > 0 && (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-center">
                      <span className="text-xs text-amber-700 dark:text-amber-300">⚠ {MAX_ATTEMPTS - attempts} attempts left</span>
                    </div>
                  )}
                  <Button type="submit" className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-base" disabled={loading || otpExpiry <= 0}>
                    {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <CheckCircle2 className="size-4 mr-2" />}
                    Verify & Enter
                  </Button>
                  <Button type="button" variant="ghost" className="w-full text-sm" onClick={() => { setStage("login"); setOtp(""); setOtpExpiry(0); }}>← Back to login</Button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
